import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { MembershipBenefit } from '@/api/config';
import { recordMembershipOfferEvent } from '@/api/generation';

interface FirstGenModalProps {
  isOpen: boolean;
  onClose: () => void;
  benefits?: MembershipBenefit[];
  generationId?: string | null;
  offerContextId?: string | null;
}

export default function FirstGenModal({
  isOpen,
  onClose,
  benefits,
  generationId,
  offerContextId,
}: FirstGenModalProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    if (isOpen && offerContextId) {
      void recordMembershipOfferEvent(offerContextId, 'impression', {
        surface: 'modal',
        gen_id: generationId,
      })
    }
  }, [generationId, isOpen, offerContextId])

  if (!isOpen) return null;

  const dismiss = () => {
    if (offerContextId) {
      void recordMembershipOfferEvent(offerContextId, 'dismiss', { surface: 'modal' })
    }
    onClose()
  }

  return (
    <div data-testid="firstgen-modal" className="fixed inset-0 z-50 flex items-center justify-center px-5">
      <div className="absolute inset-0 bg-black/50" onClick={dismiss}></div>
      <div className="relative bg-white rounded-3xl p-6 w-full max-w-sm animate-scale-in">
        {/* 装饰 */}
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-20 h-1 rounded-full"
          style={{ background: 'linear-gradient(135deg, #FF6B9D, #A855F7)' }}></div>

        <div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center"
          style={{ background: 'linear-gradient(135deg, #FFE4EF, #F3E8FF)' }}>
          <i className="ri-sparkling-2-fill text-pink-500 text-3xl"></i>
        </div>

        <h3 className="text-xl font-bold text-gray-800 text-center mb-2">{t('gen_firstGenTitle')}</h3>
        <p className="text-sm text-gray-500 text-center mb-2">{t('gen_firstGenDesc')}</p>

        {/* 会员权益 */}
        <div className="bg-gray-50 rounded-2xl p-4 mb-5 space-y-2">
          <p className="text-xs text-gray-400 font-medium mb-2">{t('gen_firstGenBenefits')}</p>
          {benefits && benefits.length > 0
            ? benefits.map((b, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: 'linear-gradient(135deg, #FF6B9D, #A855F7)' }}>
                    <i className="ri-check-line text-white text-xs"></i>
                  </div>
                  <span className="text-sm text-gray-700">{b.title_zh ?? b.title}</span>
                </div>
              ))
            : (['gen_benefit1', 'gen_benefit2', 'gen_benefit3', 'gen_benefit4'] as const).map((key) => (
                <div key={key} className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: 'linear-gradient(135deg, #FF6B9D, #A855F7)' }}>
                    <i className="ri-check-line text-white text-xs"></i>
                  </div>
                  <span className="text-sm text-gray-700">{t(key)}</span>
                </div>
              ))}
        </div>

        <div className="flex gap-3">
          <button
            data-testid="firstgen-modal-close"
            aria-label="firstgen-modal-close"
            onClick={dismiss}
            className="flex-1 py-3 rounded-xl bg-gray-100 text-gray-700 font-medium text-sm cursor-pointer whitespace-nowrap"
          >
            {t('gen_continue')}
          </button>
          <button
            onClick={() => {
              if (offerContextId) {
                void recordMembershipOfferEvent(offerContextId, 'click', { surface: 'modal' })
              }
              sessionStorage.setItem('paySource', JSON.stringify({
                route: '/profile?tab=history',
                action: 'first_generation_success_offer',
                params: { gen_id: generationId },
                offer_context_id: offerContextId,
              }));
              onClose();
              navigate('/membership');
            }}
            className="flex-1 py-3 rounded-xl text-white font-medium text-sm cursor-pointer whitespace-nowrap"
            style={{ background: 'linear-gradient(135deg, #FF6B9D, #A855F7)' }}
          >
            {t('gen_openMembership')}
          </button>
        </div>
      </div>
    </div>
  );
}
